/**
 * Enhanced Predictive Search with Fuzzy Matching & Spelling Correction
 * 
 * Features:
 * - Fuzzy matching (laroch → La Roche)
 * - Spelling suggestions
 * - Autocomplete recommendations
 * - Debounced input handling
 * 
 * Installation:
 * 1. Add this file to assets/enhanced-predictive-search.js
 * 2. Include in theme.liquid: {{ 'enhanced-predictive-search.js' | asset_url | script_tag }}
 * 3. Or add the code to your existing theme.js
 */

(function() {
  'use strict';

  // ============================================
  // CONFIGURATION - Customize these values
  // ============================================
  const CONFIG = {
    minSearchLength: 2,           // Minimum characters before search
    debounceDelay: 280,           // Delay before searching (ms)
    maxSuggestions: 5,            // Max spelling suggestions to show
    fuzzyThreshold: 0.6,          // 0-1: Higher = stricter matching
    showDidYouMean: true,         // Show "Did you mean..." suggestions
    enableLocalCache: true,       // Cache search results locally
    cacheExpiry: 5 * 60 * 1000,   // Cache expiry (5 minutes)
  };

  // Common brand name mappings for your store
  // Add your product brands here for better fuzzy matching
  const BRAND_ALIASES = {
    'laroch': 'La Roche-Posay',
    'larosch': 'La Roche-Posay',
    'laroche': 'La Roche-Posay',
    'la rosh': 'La Roche-Posay',
    'loreal': "L'Oréal",
    'lorial': "L'Oréal",
    'cerave': 'CeraVe',
    'serave': 'CeraVe',
    'neutragena': 'Neutrogena',
    'nuetrogena': 'Neutrogena',
    'vichy': 'Vichy',
    'vichi': 'Vichy',
    'bioderma': 'Bioderma',
    'byoderma': 'Bioderma',
    'avene': 'Avène',
    'aven': 'Avène',
    'eucerin': 'Eucerin',
    'ucerine': 'Eucerin',
    'garnier': 'Garnier',
    'garneir': 'Garnier',
    'maybelline': 'Maybelline',
    'maybeline': 'Maybelline',
    'nivea': 'Nivea',
    'nevea': 'Nivea',
    'olay': 'Olay',
    'olai': 'Olay',
    // Add more brand aliases as needed
  };

  // ============================================
  // FUZZY MATCHING UTILITIES
  // ============================================
  
  /**
   * Calculate Levenshtein distance between two strings
   * Lower distance = more similar
   */
  function levenshteinDistance(str1, str2) {
    const s1 = str1.toLowerCase();
    const s2 = str2.toLowerCase();
    
    if (s1 === s2) return 0;
    if (s1.length === 0) return s2.length;
    if (s2.length === 0) return s1.length;

    const matrix = [];

    for (let i = 0; i <= s2.length; i++) {
      matrix[i] = [i];
    }

    for (let j = 0; j <= s1.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= s2.length; i++) {
      for (let j = 1; j <= s1.length; j++) {
        if (s2.charAt(i - 1) === s1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1, // substitution
            matrix[i][j - 1] + 1,     // insertion
            matrix[i - 1][j] + 1      // deletion
          );
        }
      }
    }

    return matrix[s2.length][s1.length];
  }

  /**
   * Calculate similarity score (0-1)
   * Higher = more similar
   */
  function similarityScore(str1, str2) {
    const distance = levenshteinDistance(str1, str2);
    const maxLength = Math.max(str1.length, str2.length);
    return maxLength === 0 ? 1 : 1 - (distance / maxLength);
  }

  /**
   * Find best matching brand from aliases
   */
  function findBrandMatch(query) {
    const normalizedQuery = query.toLowerCase().trim();
    
    // Direct alias match
    if (BRAND_ALIASES[normalizedQuery]) {
      return BRAND_ALIASES[normalizedQuery];
    }
    
    // Fuzzy alias match
    let bestMatch = null;
    let bestScore = CONFIG.fuzzyThreshold;
    
    for (const [alias, brand] of Object.entries(BRAND_ALIASES)) {
      const score = similarityScore(normalizedQuery, alias);
      if (score > bestScore) {
        bestScore = score;
        bestMatch = brand;
      }
    }
    
    return bestMatch;
  }

  /**
   * Generate spelling suggestions
   */
  function getSpellingSuggestions(query, terms) {
    const suggestions = [];
    const normalizedQuery = query.toLowerCase().trim();
    
    // Check brand aliases first
    const brandMatch = findBrandMatch(normalizedQuery);
    if (brandMatch && brandMatch.toLowerCase() !== normalizedQuery) {
      suggestions.push(brandMatch);
    }
    
    // Find similar terms from provided list
    if (terms && terms.length > 0) {
      const scored = terms
        .map(term => ({
          term,
          score: similarityScore(normalizedQuery, term.toLowerCase())
        }))
        .filter(item => item.score >= CONFIG.fuzzyThreshold && item.term.toLowerCase() !== normalizedQuery)
        .sort((a, b) => b.score - a.score)
        .slice(0, CONFIG.maxSuggestions);
      
      scored.forEach(item => {
        if (!suggestions.includes(item.term)) {
          suggestions.push(item.term);
        }
      });
    }
    
    return suggestions.slice(0, CONFIG.maxSuggestions);
  }

  /**
   * Normalize search query for better matching
   */
  function normalizeQuery(query) {
    let normalized = query.toLowerCase().trim();
    
    // Check if there's a brand alias match
    const brandMatch = findBrandMatch(normalized);
    if (brandMatch) {
      return brandMatch;
    }
    
    // Basic normalization
    normalized = normalized
      .replace(/['']/g, "'")    // Normalize apostrophes
      .replace(/\s+/g, ' ')      // Normalize spaces
      .replace(/[^\w\s'-]/g, ''); // Remove special chars except apostrophe and hyphen
    
    return normalized;
  }

  // ============================================
  // CACHING UTILITIES
  // ============================================
  
  const searchCache = new Map();

  function getCached(key) {
    if (!CONFIG.enableLocalCache) return null;
    
    const cached = searchCache.get(key);
    if (cached && Date.now() - cached.timestamp < CONFIG.cacheExpiry) {
      return cached.data;
    }
    searchCache.delete(key);
    return null;
  }

  function setCache(key, data) {
    if (!CONFIG.enableLocalCache) return;
    
    searchCache.set(key, {
      data,
      timestamp: Date.now()
    });
    
    // Limit cache size
    if (searchCache.size > 100) {
      const firstKey = searchCache.keys().next().value;
      searchCache.delete(firstKey);
    }
  }

  // ============================================
  // ENHANCED PREDICTIVE SEARCH CLASS
  // ============================================
  
  class EnhancedPredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.searchForm = null;
      this.queryInput = null;
      this.resultsContainer = null;
      this.knownTerms = []; // Store known search terms for suggestions
      
      this.attachShadow({ mode: 'open' });
      this.shadowRoot.appendChild(
        document.createRange().createContextualFragment(`
          <style>
            :host { display: block; }
            .did-you-mean {
              padding: 0.75rem 1rem;
              background: #f8f9fa;
              border-bottom: 1px solid #e9ecef;
              font-size: 0.875rem;
            }
            .did-you-mean strong { cursor: pointer; color: #007bff; }
            .did-you-mean strong:hover { text-decoration: underline; }
            .suggestion-item {
              display: inline-block;
              margin-right: 0.5rem;
              padding: 0.25rem 0.5rem;
              background: #e9ecef;
              border-radius: 4px;
              cursor: pointer;
              font-size: 0.8rem;
            }
            .suggestion-item:hover { background: #dee2e6; }
          </style>
          <slot name="did-you-mean"></slot>
          <slot name="results"></slot>
        `)
      );
    }

    connectedCallback() {
      this.abortController = new AbortController();
      this.searchForm = document.querySelector(`[aria-owns="${this.id}"]`);
      
      if (!this.searchForm) return;
      
      this.queryInput = this.searchForm.elements['q'];
      this.resultsContainer = this.querySelector('[slot="results"]');
      
      // Debounced input handler
      let timeoutId;
      this.queryInput.addEventListener('input', (e) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => this.onInputChanged(), CONFIG.debounceDelay);
      }, { signal: this.abortController.signal });

      this.searchForm.addEventListener('submit', (e) => this.onFormSubmitted(e), 
        { signal: this.abortController.signal });
      
      this.searchForm.addEventListener('reset', () => this.onSearchCleared(), 
        { signal: this.abortController.signal });
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    supportsPredictiveApi() {
      try {
        const features = JSON.parse(document.getElementById('shopify-features').innerHTML);
        return features.predictiveSearch;
      } catch (e) {
        return false;
      }
    }

    async onInputChanged() {
      const rawQuery = this.queryInput.value.trim();
      
      if (rawQuery.length < CONFIG.minSearchLength) {
        this.onSearchCleared();
        return;
      }

      // Normalize and potentially correct the query
      const normalizedQuery = normalizeQuery(rawQuery);
      const shouldUseNormalized = normalizedQuery.toLowerCase() !== rawQuery.toLowerCase();
      
      // Check cache first
      const cacheKey = normalizedQuery.toLowerCase();
      const cached = getCached(cacheKey);
      if (cached) {
        this.displayResults(cached, rawQuery, shouldUseNormalized ? normalizedQuery : null);
        return;
      }

      // Perform search
      await this.doSearch(normalizedQuery, rawQuery, shouldUseNormalized);
    }

    async doSearch(query, originalQuery, correctedQuery = null) {
      document.documentElement.dispatchEvent(
        new CustomEvent('theme:loading:start', { bubbles: true })
      );

      try {
        const searchEndpoint = this.supportsPredictiveApi() 
          ? `${window.Shopify.routes.root}search/suggest`
          : `${window.Shopify.routes.root}search`;

        // Build search URL with fuzzy-friendly params
        const params = new URLSearchParams({
          q: query,
          section_id: 'predictive-search',
          'resources[type]': 'product,collection,article,page',
          'resources[limit]': '10',
          'resources[options][unavailable_products]': 'last',
          'resources[options][fields]': 'title,vendor,product_type,tag,variants.sku,variants.title'
        });

        const response = await fetch(`${searchEndpoint}?${params.toString()}`, {
          signal: this.abortController.signal
        });

        if (!response.ok) throw new Error('Search failed');

        const html = await response.text();
        
        // Cache the result
        setCache(query.toLowerCase(), html);
        
        this.displayResults(html, originalQuery, correctedQuery);
        
        // Extract terms for future suggestions
        this.extractSearchTerms(html);

      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Search error:', error);
        }
      } finally {
        document.documentElement.dispatchEvent(
          new CustomEvent('theme:loading:end', { bubbles: true })
        );
      }
    }

    displayResults(html, originalQuery, correctedQuery = null) {
      if (!this.resultsContainer) return;

      // Parse the HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const content = doc.querySelector('.predictive-search__content, .shopify-section');
      
      // Clear previous results
      this.resultsContainer.innerHTML = '';

      // Add "Did you mean" suggestion if query was corrected
      if (CONFIG.showDidYouMean && correctedQuery) {
        const didYouMean = this.createDidYouMeanElement(originalQuery, correctedQuery);
        this.resultsContainer.appendChild(didYouMean);
      }

      // Add spelling suggestions for no/few results
      const suggestions = getSpellingSuggestions(originalQuery, this.knownTerms);
      if (suggestions.length > 0 && (!content || content.textContent.includes('no_results'))) {
        const suggestionsEl = this.createSuggestionsElement(suggestions);
        this.resultsContainer.appendChild(suggestionsEl);
      }

      // Add actual results
      if (content) {
        this.resultsContainer.appendChild(content.cloneNode(true));
      }

      // Show the results container
      this.setAttribute('open', '');
    }

    createDidYouMeanElement(original, corrected) {
      const div = document.createElement('div');
      div.className = 'enhanced-search__did-you-mean';
      div.innerHTML = `
        <div style="padding: 0.75rem 1rem; background: #f0f7ff; border-radius: 4px; margin-bottom: 1rem; font-size: 0.9rem;">
          <span>Searching for "<strong style="cursor: pointer; color: #0066cc;">${corrected}</strong>" instead of "${original}"</span>
          <button type="button" style="margin-left: 0.5rem; background: none; border: none; color: #666; cursor: pointer; font-size: 0.8rem;">
            Search for "${original}" instead
          </button>
        </div>
      `;
      
      // Click handler for correction
      div.querySelector('strong').addEventListener('click', () => {
        this.queryInput.value = corrected;
        this.onInputChanged();
      });
      
      // Click handler for original
      div.querySelector('button').addEventListener('click', () => {
        this.doSearch(original, original, null);
      });
      
      return div;
    }

    createSuggestionsElement(suggestions) {
      const div = document.createElement('div');
      div.className = 'enhanced-search__suggestions';
      div.innerHTML = `
        <div style="padding: 0.75rem 1rem; border-bottom: 1px solid #eee;">
          <span style="color: #666; font-size: 0.9rem;">Did you mean: </span>
          ${suggestions.map(s => `
            <span class="suggestion-chip" style="display: inline-block; margin: 0.25rem; padding: 0.25rem 0.75rem; background: #f0f0f0; border-radius: 20px; cursor: pointer; font-size: 0.85rem;">
              ${s}
            </span>
          `).join('')}
        </div>
      `;
      
      // Add click handlers
      div.querySelectorAll('.suggestion-chip').forEach(chip => {
        chip.addEventListener('click', () => {
          this.queryInput.value = chip.textContent.trim();
          this.onInputChanged();
        });
      });
      
      return div;
    }

    extractSearchTerms(html) {
      // Extract product titles and vendor names from results
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      doc.querySelectorAll('[data-product-title], .product-card__title, h3, h4').forEach(el => {
        const text = el.textContent.trim();
        if (text && !this.knownTerms.includes(text)) {
          this.knownTerms.push(text);
        }
      });
      
      // Keep list manageable
      if (this.knownTerms.length > 500) {
        this.knownTerms = this.knownTerms.slice(-500);
      }
    }

    onFormSubmitted(event) {
      if (this.queryInput.value.trim() === '') {
        event.preventDefault();
        return;
      }
      
      // Apply normalized query to form submission
      const normalizedQuery = normalizeQuery(this.queryInput.value);
      if (normalizedQuery !== this.queryInput.value) {
        this.queryInput.value = normalizedQuery;
      }
    }

    onSearchCleared() {
      this.removeAttribute('open');
      if (this.resultsContainer) {
        this.resultsContainer.innerHTML = '';
      }
    }
  }

  // Register the custom element
  if (!customElements.get('enhanced-predictive-search')) {
    customElements.define('enhanced-predictive-search', EnhancedPredictiveSearch);
  }

  // ============================================
  // UPGRADE EXISTING PREDICTIVE SEARCH
  // ============================================
  
  /**
   * Enhance existing predictive-search elements
   * This upgrades the default Shopify search without replacing it
   */
  function enhanceExistingSearch() {
    document.querySelectorAll('predictive-search').forEach(searchEl => {
      // Skip if already enhanced
      if (searchEl.hasAttribute('data-enhanced')) return;
      searchEl.setAttribute('data-enhanced', 'true');

      const form = document.querySelector(`[aria-owns="${searchEl.id}"]`);
      if (!form) return;

      const input = form.elements['q'];
      if (!input) return;

      // Store original value
      let originalValue = '';
      let lastNormalized = '';

      // Pre-process input before search
      input.addEventListener('input', (e) => {
        originalValue = input.value;
        const normalized = normalizeQuery(input.value);
        
        // If brand correction found, show subtle indication
        if (normalized !== input.value && normalized !== lastNormalized) {
          lastNormalized = normalized;
          showSearchHint(searchEl, `Searching for: ${normalized}`);
        }
      }, { capture: true });

      // Intercept form submission to apply corrections
      form.addEventListener('submit', (e) => {
        const normalized = normalizeQuery(input.value);
        if (normalized !== input.value) {
          input.value = normalized;
        }
      }, { capture: true });
    });
  }

  function showSearchHint(searchEl, message) {
    // Find or create hint element
    let hint = searchEl.querySelector('.search-hint');
    if (!hint) {
      hint = document.createElement('div');
      hint.className = 'search-hint';
      hint.style.cssText = `
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        padding: 0.5rem 1rem;
        background: #fffbeb;
        border: 1px solid #fbbf24;
        border-radius: 4px;
        font-size: 0.8rem;
        color: #92400e;
        z-index: 100;
      `;
      searchEl.style.position = 'relative';
      searchEl.appendChild(hint);
    }
    
    hint.textContent = message;
    hint.style.display = 'block';

    // Auto-hide after 3 seconds
    clearTimeout(hint._hideTimeout);
    hint._hideTimeout = setTimeout(() => {
      hint.style.display = 'none';
    }, 3000);
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enhanceExistingSearch);
  } else {
    enhanceExistingSearch();
  }

  // Also observe for dynamically added search elements
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) {
          if (node.matches?.('predictive-search')) {
            enhanceExistingSearch();
          }
          node.querySelectorAll?.('predictive-search').forEach(() => {
            enhanceExistingSearch();
          });
        }
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });

  // Export for external use
  window.EnhancedSearch = {
    normalizeQuery,
    similarityScore,
    findBrandMatch,
    getSpellingSuggestions,
    addBrandAlias: (alias, brand) => {
      BRAND_ALIASES[alias.toLowerCase()] = brand;
    },
    config: CONFIG
  };

})();